import React from 'react';
import { Segmented } from 'antd';

import { isAdmin } from '../../utils';
import { canManage } from '../../utils/permissions';

/**
 * The three tiers a card layout cascades through (cardLayouts — a tile, a
 * table), and who may write each: the one tier UI both editors share, so
 * "This card · This collection · Just me" reads and gates the same wherever a
 * card's arrangement is changed. Extracted from TileLayoutModal when the
 * table's Columns editor became its second user.
 */
export const LAYOUT_TIERS = [
	{ value: 'card', label: 'This card' },
	{ value: 'collection', label: 'This collection' },
	{ value: 'user', label: 'Just me' },
];

export const TIER_NOTE = {
	card: 'Everyone who uses this card, in every collection.',
	collection: 'Everyone in this collection. Overrides the card.',
	user: 'Only you. Overrides the collection and the card.',
};

/**
 * Which tiers this caller may write, and the sentence for each they may not.
 * The card tier is the card's own write rule, which makes a GLOBAL card's
 * admin-only — and that is exactly why the collection tier exists. The API
 * runs the same gates (cardLayoutUtils assertMayWriteTier →
 * assertCardDefWritable, where an admin passes first).
 *
 * The collection and user tiers are anchored to a collection, so they need
 * one in hand: the card page has none, and says where to go instead.
 */
export function layoutTierAccess({ card, collection, collectionId, admin = isAdmin() }) {
	const global = card?.scope === 'global';
	return {
		allowed: {
			card: global ? admin : (admin || (collection ? canManage(collection) : true)),
			collection: Boolean(collectionId) && Boolean(collection) && canManage(collection),
			user: Boolean(collectionId),
		},
		refusal: {
			card: global
				? (collectionId
					? 'Global cards are keepr-managed — arrange it for this collection instead.'
					: 'Global cards are keepr-managed — open a collection’s list to arrange it there.')
				: 'Only a manager of the card’s collection can change it for everyone.',
			collection: collectionId
				? 'Only a collection manager can change it for everyone here.'
				: 'Open a collection’s list to arrange it for everyone there.',
			user: 'Open a collection’s list to arrange it for yourself.',
		},
	};
}

/** The most specific tier this caller may write — the one they came to change. */
export const preferredTier = allowed => ['user', 'collection', 'card'].find(key => allowed[key]) || 'card';

/** The tier this one falls back to, if it holds nothing: the rows beneath it. */
export const tiersBelow = tier => (tier === 'user' ? ['collection', 'card'] : tier === 'collection' ? ['card'] : []);

export function LayoutTierSwitch({ value, onChange, allowed, refusal = null, className = 'tile-editor__tiers' }) {
	return (
		<Segmented
			className={className}
			value={value}
			onChange={onChange}
			options={LAYOUT_TIERS.map(option => ({
				...option,
				disabled: !allowed[option.value],
				// A tier this caller cannot write says why on hover.
				title: allowed[option.value] ? option.label : refusal?.[option.value],
			}))}
		/>
	);
}
